
/**
 * @fileoverview Centralized constants for the Character domain.
 */

import type { CharacterV2 } from "@/domains/characters/schemas";

export const CHARACTER_CATEGORIES_V2: { [key in CharacterV2['category']]: { label: string; description: string; } } = {
    major: {
        label: "Major Characters",
        description: "Significant characters who have a major impact on the plot but are not the central focus."
    },
    minor: {
        label: "Minor Characters",
        description: "Supporting characters who appear in a few scenes and serve a specific purpose."
    },
    extra: {
        label: "Extras",
        description: "Background characters with minimal impact on the story."
    }
};

/**
 * Image styles and shot types - canonical source is the visuals domain.
 */
export { CHARACTER_IMAGE_STYLES, CHARACTER_IMAGE_SHOT_TYPES } from '@/domains/visuals/constants';


export const SHAPE_DEFINITIONS = {
  'Skeletal': { label: 'Skeletal', value: 1, description: 'Extremely thin to the point of being gaunt.' },
  'Thin': { label: 'Thin', value: 2, description: 'Very slender with little body fat or muscle mass.' },
  'Slender': { label: 'Slender', value: 3, description: 'Gracefully thin; lithe and slim.' },
  'Lean': { label: 'Lean', value: 4, description: 'Low body fat, often with visible but not bulky muscle.' },
  'Average': { label: 'Average', value: 5, description: 'A balanced build, neither excessively thin nor overweight.' },
  'Plump': { label: 'Plump', value: 7, description: 'Softly rounded; carrying noticeable extra weight.' },
  'Overweight': { label: 'Overweight', value: 8, description: 'Clearly carrying substantial excess mass' },
  'Massive': { label: 'Massive', value: 10, description: 'Extremely large and heavy-set, in height, width, or both.' },
} as const;

export const ATHLETICISM_DEFINITIONS = {
  'Atrophied': { label: 'Atrophied', value: 1, description: 'Muscles have wasted away due to disuse or illness.' },
  'Sedentary': { label: 'Sedentary', value: 2, description: 'Inactive; showing no signs of regular physical activity.' },
  'Sluggish': { label: 'Sluggish', value: 3, description: 'Slow-moving and lacking energy.' },
  'Average': { label: 'Average', value: 5, description: 'A normal level of fitness; not notably athletic or unathletic.' },
  'Fit': { label: 'Fit', value: 6, description: 'Healthy and in good physical shape.' },
  'Toned': { label: 'Toned', value: 7, description: 'Visible muscle definition without being bulky.' },
  'Athletic': { label: 'Athletic', value: 8, description: 'Visibly strong and capable; built for performance and activity.' },
  'Elite': { label: 'Elite', value: 10, description: 'The peak of physical conditioning for a specific sport or activity.' },
} as const;

export const SKINTONE_DEFINITIONS = {
  'Porcelain': { label: 'Porcelain', value: 1, description: 'Extremely fair and translucent skin, minimal pigmentation.' },
  'Ivory': { label: 'Ivory', value: 2, description: 'Very light skin tone, often with cool or slightly rosy undertones.' },
  'Beige': { label: 'Beige', value: 3, description: 'Standard light complexion, neutral fair tone.' },
  'Honey': { label: 'Honey', value: 4, description: 'A warm, light golden-brown tone, radiant complexion.' },
  'Sienna': { label: 'Sienna', value: 6, description: 'Medium, rich brown with golden and reddish undertones.' },
  'Caramel': { label: 'Caramel', value: 7, description: 'A smooth, deep medium-brown with strong golden or yellow undertones.' },
  'Tawny': { label: 'Tawny', value: 8, description: 'A deep, rich golden-brown hue, darker than caramel.' },
  'Ebony': { label: 'Ebony', value: 10, description: 'The deepest brown tone, nearing black, with rich undertones.' },
} as const;

export const CHEST_DEFINITIONS = {
  'Very Narrow': { label: 'Very Narrow', value: 1, description: 'Notably small chest circumference; flat or minimal upper torso width.' },
  'Narrow': { label: 'Narrow', value: 3, description: 'Below average chest width; slim upper torso.' },
  'Average': { label: 'Average', value: 5, description: 'Typical chest proportions for body type.' },
  'Wide': { label: 'Wide', value: 7, description: 'Above average chest width; broad or full upper torso.' },
  'Very Wide': { label: 'Very Wide', value: 10, description: 'Notably large chest circumference; very broad or full upper torso.' },
} as const;

export const WAIST_DEFINITIONS = {
  'Very Narrow': { label: 'Very Narrow', value: 1, description: 'Exceptionally small waist; highly defined midsection.' },
  'Narrow': { label: 'Narrow', value: 3, description: 'Below average waist circumference; trim midsection.' },
  'Average': { label: 'Average', value: 5, description: 'Typical waist proportions for body type.' },
  'Wide': { label: 'Wide', value: 7, description: 'Above average waist circumference; thicker midsection.' },
  'Very Wide': { label: 'Very Wide', value: 10, description: 'Notably large waist; broad midsection.' },
} as const;

export const HIPS_DEFINITIONS = {
  'Very Narrow': { label: 'Very Narrow', value: 1, description: 'Notably small hip circumference; very slim lower torso.' },
  'Narrow': { label: 'Narrow', value: 3, description: 'Below average hip width; slim lower torso.' },
  'Average': { label: 'Average', value: 5, description: 'Typical hip proportions for body type.' },
  'Wide': { label: 'Wide', value: 7, description: 'Above average hip width; full lower torso.' },
  'Very Wide': { label: 'Very Wide', value: 10, description: 'Notably large hip circumference; very full lower torso.' },
} as const;

/**
 * @deprecated Use BODY_ARCHETYPES instead.
 */
export const BODY_ARCHETYPE_OPTIONS = [
  'Androgynous & Striking',
  'Athletic & Intense',
  'Average & Approachable',
  'Bodybuilder',
  'Curvaceous & Confident',
  'Energetic & Bubbly',
  'Ethereal & Delicate',
  'Petite & Delicate Frame',
  'Petite & Dynamic',
  'Powerful & Imposing',
  'Slender & Graceful',
  'Slender & Toned',
  'Tall & Commanding',
] as const;

/**
 * @deprecated Use CHARACTER_ARCHETYPES instead.
 */
export const CHARACTER_ARCHETYPE_OPTIONS = [
  'Action Hero/Warrior',
  'Antagonist/Complex Villain',
  'Comedic Force/Sidekick',
  'Dramatic Powerhouse',
  'Femme Fatale/Siren',
  'Girl/Boy Next Door/Relatable Protagonist',
  'Ingenue/Youthful Lead',
  'Mentor/Matriarch/Authority Figure',
  'Quirky/Eccentric Character',
  'Romantic Lead/Love Interest',
] as const;

export const BODY_ARCHETYPES = [
  { name: "Petite Slight", description: "Small-statured with a narrow, light-built frame and minimal overall mass." },
  { name: "Petite Athletic", description: "Small-statured with clear fitness, agility, and a toned, sporty build." },
  { name: "Petite Muscular", description: "Small-statured with dense visible muscle and compact physical power." },
  { name: "Short Stocky", description: "Shorter in height with a thickset, sturdy, and compactly heavy build." },
  { name: "Short Heavyset", description: "Shorter in height with clearly fuller, softer, and heavier overall body mass." },
  { name: "Compact Lean", description: "Below average height with a slim, taut, wiry, or lightly built frame." },
  { name: "Compact Athletic", description: "Below average height with evident conditioning, agility, and trained movement." },
  { name: "Compact Muscular", description: "Below average height with dense muscle and a strong, compact build." },
  { name: "Compact Broad/Stocky", description: "Below average height with noticeable width, sturdiness, and thicker-set proportions." },
  { name: "Average Slight", description: "Average height with a narrow, light, or delicate frame and low overall mass." },
  { name: "Average Lean", description: "Average height with a slim, trim, wiry, or low-body-fat build." },
  { name: "Average Proportional", description: "Average height with balanced proportions and no strongly dominant build trait." },
  { name: "Average Athletic", description: "Average height with visible fitness, body control, and a toned active physique." },
  { name: "Average Muscular", description: "Average height with clearly developed muscle mass beyond simply fit or toned." },
  { name: "Average Broad", description: "Average height with a naturally wide frame, broad shoulders, or large chest structure." },
  { name: "Average Stocky", description: "Average height with a thickset, sturdy, and compactly powerful build." },
  { name: "Average Heavyset", description: "Average height with substantial fuller or softer mass and a heavier overall build." },
  { name: "Curvy / Full", description: "A fuller body shape defined by rounded, shapely contours rather than width or muscularity alone." },
  { name: "Tall Lean", description: "Tall with a slim, elongated, and lightly built frame." },
  { name: "Tall Athletic", description: "Tall with visible conditioning, sportiness, and an evidently trained physique." },
  { name: "Tall Muscular", description: "Tall with significant muscle mass and a strongly built physical frame." },
  { name: "Tall Broad", description: "Tall with notable width, a large frame, and broad or substantial proportions." },
  { name: "Tall Heavyset", description: "Tall with clearly heavier, fuller, or softer overall body mass." },
  { name: "Towering Slender", description: "Exceptionally tall with a narrow, light-built, and elongated frame." },
  { name: "Towering Athletic", description: "Exceptionally tall with clear athletic conditioning and trained physicality." },
  { name: "Towering Massive", description: "Exceptionally tall with very large overall mass, whether broad, muscular, or heavily built." },
] as const;

export const CHARACTER_ARCHETYPES = [
  { name: "Action Hero / Warrior", description: "A physical lead defined by courage, combat readiness, protection, survival, or forceful action." },
  { name: "Relatable Protagonist / Everyman", description: "A grounded, accessible central character whose ordinariness or emotional honesty invites audience identification." },
  { name: "Romantic Lead / Love Interest", description: "A character primarily framed through romance, attraction, longing, or intimate emotional connection." },
  { name: "Ingenue / Youthful Lead", description: "A young, fresh, vulnerable, idealistic, or coming-of-age character with youthful lead energy." },
  { name: "Dramatic Lead / Powerhouse", description: "An emotionally intense, high-stakes, commanding, or tragedy-driven character at the center of dramatic material." },
  { name: "Rebel / Antihero", description: "A defiant, morally ambiguous, sharp-edged, or outsider character who resists rules and conventions." },
  { name: "Comedic Relief / Sidekick", description: "A humorous supporting character who brings levity, energy, or loyal companionship to the story." },
  { name: "Quirky / Eccentric Character", description: "An offbeat, unusual, highly idiosyncratic character whose distinctiveness stands out in tone or behavior." },
  { name: "Best Friend / Confidant", description: "A trusted companion who provides emotional support, loyalty, advice, or personal intimacy to a central character." },
  { name: "Mentor / Guide", description: "A teacher, elder, trainer, or wisdom-bearing figure who helps direct another character's growth or path." },
  { name: "Authority Figure / Leader", description: "A commanding institutional, social, or familial presence such as a boss, commander, parent, judge, or leader." },
  { name: "Villain / Mastermind", description: "A primary opposing force who drives conflict through malice, control, manipulation, domination, or calculated intent." },
  { name: "Complex Antagonist / Rival", description: "An opposing character defined by rivalry, conflict, or moral complexity rather than pure villainy." },
  { name: "Enforcer / Heavy", description: "A physically intimidating or force-oriented character who serves as muscle, threat, bodyguard, or blunt instrument." },
  { name: "Femme Fatale / Seducer", description: "A seductive, alluring, and often dangerous character who wields attraction, mystery, or erotic power strategically." },
  { name: "Charismatic Operator / Social Chameleon", description: "A socially adept, persuasive, adaptable character who navigates power through charm, manipulation, or strategic persona shifts." },
  { name: "Caretaker / Matriarch / Patriarch", description: "A stabilizing, protective, nurturing, or family-centered figure who serves as an emotional or structural anchor." },
] as const;

/** Flat array of body archetype names for use in zod enums and selects. */
export const BODY_ARCHETYPE_NAMES = BODY_ARCHETYPES.map(a => a.name) as unknown as readonly [string, ...string[]];

/** Flat array of character archetype names for use in zod enums and selects. */
export const CHARACTER_ARCHETYPE_NAMES = CHARACTER_ARCHETYPES.map(a => a.name) as unknown as readonly [string, ...string[]];

/**
 * Ethnicity classification with L1 (broad group) and L2 (sub-ethnicity) levels.
 *
 * Structure mirrors the `ethnicities` field on PersonDataProfile:
 *   `Record<string, string[]>` → L1 name as key, L2 names as values.
 *
 * Consumers:
 *  - Use `ETHNICITY_L1_NAMES` for a flat list of top-level group names (dropdowns, zod enums).
 *  - Use `ETHNICITY_L2_BY_L1[l1Name]` to get sub-ethnicities for a selected L1.
 *  - Use `ETHNICITY_ALL_L2_NAMES` for a flat list of every L2 value (search / autocomplete).
 */
export const ETHNICITY_DEFINITIONS = [
  {
    name: "Caucasian",
    subEthnicities: [
      "Germanic (German, Austrian, Dutch, Swiss-German)",
      "Scandinavian / North Germanic (Swedish, Danish, Norwegian, Icelandic)",
      "Celtic (Irish, Scottish, Welsh, Breton)",
      "Anglo-Saxon (English)",
      "Gallo-Romance (French, Walloon/Belgian)",
      "Ibero-Romance (Spanish, Portuguese)",
      "Italo-Romance (Italian, Sicilian)",
      "Eastern Romance / Balkan Romance (Romania)",
      "West Slavic & Hungarian (Polish, Czech, Slovak, Hungarian)",
      "East Slavic (Russian, Ukrainian, Belarusian)",
      "South Slavic (Serbian, Croatian, Bosnian, Slovenian, Macedonian)",
      "Baltic (Lithuanian, Latvian, Estonian)",
      "Finnic (Finnish)",
      "Hellenic (Greek)",
      "Albanian / Illyrian (Albanian)",
      "Ashkenazi Jewish",
      "Sephardic Jewish",
      "Kartvelian / Georgian",
      "Armenian",
    ],
  },
  {
    name: "East Asian",
    subEthnicities: [
      "Han Chinese",
      "Yamato / Japanese",
      "Korean",
    ],
  },
  {
    name: "Southeast Asian",
    subEthnicities: [
      "Filipino",
      "Austronesian (Malay, Indonesian)",
      "Javanese",
      "Kinh / Vietnamese",
      "Tai / Thai",
      "Hmong",
      "Khmer / Cambodian",
      "Burmese / Bamar",
    ],
  },
  {
    name: "South Asian",
    subEthnicities: [
      "Punjabi",
      "Gujarati",
      "Bengali",
      "Tamil",
      "Telugu",
      "Malayalam",
      "Dardic / Kashmiri",
      "Himalayan / Tibeto-Burman",
      "Parsi",
      "Dravidian",
      "Indo-Aryan (Romani)",
      "Indo-Aryan (General/Other)",
    ],
  },
  {
    name: "Central & North Asian",
    subEthnicities: [
      "Turkic (Uzbek, Kazakh, Kyrgyz, Uyghur, Tatar)",
      "Mongolic (Mongolian, Buryat)",
      "Siberian / Tungusic (Yakut, Inuit-Yupik)",
    ],
  },
  {
    name: "Middle Eastern",
    subEthnicities: [
      "Arab (Levantine) (Lebanese, Syrian, Palestinian, Jordanian)",
      "Arab (Mesopotamian) (Irak, Kuwait)",
      "Arab (Peninsular) (Saudi, Emirati, Yemeni)",
      "Persian / Iranian",
      "Turkish / Anatolian",
      "Assyrian",
      "Mizrahi Jewish",
    ],
  },
  {
    name: "North African",
    subEthnicities: [
      "Maghrebi / Berber (Amazigh) (Moroccan, Algerian, Tunisian)",
      "Egyptian / Coptic",
    ],
  },
  {
    name: "Sub-Saharan African",
    subEthnicities: [
      "West African",
      "Bantu",
      "Nilotic",
      "Cushitic",
      "Habesha / Ethiosemitic",
      "Sudanic / Nubian",
    ],
  },
  {
    name: "African-American",
    subEthnicities: [],
  },
  {
    name: "Afro-Caribbean (Non-Hispanic)",
    subEthnicities: [
      "Anglophone Caribbean (Jamaican, Trinidadian, Barbadian, Bahamian)",
      "Francophone Caribbean (Haitian, Martiniquais, Guadeloupean)",
      "Surinamese / Dutch Caribbean",
    ],
  },
  {
    name: "Afro-Latino",
    subEthnicities: [
      "Afro-Caribbean (Hispanic) (Afro-Cuban, Afro-Dominican, Afro-Puerto Rican)",
      "Afro-Mesoamerican (Afro-Mexican, Afro-Panamanian, Garifuna/Honduran)",
      "Afro-Andean (Afro-Colombian, Afro-Venezuelan, Afro-Peruvian, Afro-Ecuadorian)",
      "Afro-Brazilian (Lusophone)",
    ],
  },
  {
    name: "Hispanic / Latino",
    subEthnicities: [
      "Mesoamerican (Mexican, Guatemalan, Honduran)",
      "Caribbean Hispanic (Puerto Rican, Cuban, Dominican)",
      "Andean (Colombian, Peruvian, Ecuadorian, Bolivian)",
      "Southern Cone (Argentinian, Uruguayan, Chilean)",
      "Brazilian (Lusophone)",
    ],
  },
  {
    name: "Native American / Indigenous Peoples",
    subEthnicities: [
      "Iroquoian (Mohawk, Cherokee)",
      "Siouan (Lakota, Sioux)",
      "Algonquian (Cree, Anishinaabe, Blackfoot)",
      "Muskogean (Creek)",
      "Plateau / Sahaptian (Nez Perce)",
      "Na-Dené / Navajo",
      "Tsimshianic",
      "Uto-Aztecan (Comanche)",
      "Caddoan (Pawnee)",
      "Pacific Northwest",
      "Indigenous Latin American (Mixtec, Quechua)",
      "Mapuche",
      "Indig. Brazilian",
    ],
  },
  {
    name: "Polynesian / Pacific Islander",
    subEthnicities: [
      "Maori",
      "Native Hawaiian",
      "Samoan",
    ],
  },
] as const;

/** Flat array of L1 ethnicity group names for use in zod enums and selects. */
export const ETHNICITY_L1_NAMES = ETHNICITY_DEFINITIONS.map(e => e.name) as unknown as readonly [string, ...string[]];

/** Lookup from L1 name → its L2 sub-ethnicity names. */
export const ETHNICITY_L2_BY_L1: Record<string, readonly string[]> = Object.fromEntries(
  ETHNICITY_DEFINITIONS.map(e => [e.name, e.subEthnicities])
);

/** Flat array of every L2 sub-ethnicity name (useful for search / autocomplete). */
export const ETHNICITY_ALL_L2_NAMES: readonly string[] = ETHNICITY_DEFINITIONS.flatMap(e => e.subEthnicities);

export const EYE_COLOR_OPTIONS = [
  'Brown',
  'Dark Brown',
  'Light Brown',
  'Hazel',
  'Green',
  'Blue',
  'Gray',
  'Amber',
  'Black',
] as const;

export const HAIR_COLOR_DEFINITIONS = {
  'Black': { label: 'Black', value: 1, description: 'The deepest, darkest hair color with no visible brown tones.' },
  'Dark Brown': { label: 'Dark Brown', value: 3, description: 'A rich, deep brown that appears nearly black in low light.' },
  'Brown': { label: 'Brown', value: 5, description: 'A classic mid-tone brown, the most common natural hair color.' },
  'Light Brown': { label: 'Light Brown', value: 7, description: 'A softer brown with warm, lighter undertones.' },
  'Auburn': { label: 'Auburn', value: 6, description: 'A warm reddish-brown blend, darker than copper.' },
  'Red': { label: 'Red', value: 6, description: 'Distinctly red or ginger toned natural hair color.' },
  'Strawberry Blonde': { label: 'Strawberry Blonde', value: 8, description: 'A light coppery blonde with subtle red-gold undertones.' },
  'Blonde': { label: 'Blonde', value: 9, description: 'A naturally light golden or sandy hair color.' },
  'Fake Blonde': { label: 'Fake Blonde', value: 9, description: 'Artificially lightened blonde, often brighter or more uniform than natural.' },
  'Platinum Blonde': { label: 'Platinum Blonde', value: 10, description: 'The lightest blonde shade, nearly white with a cool silvery tone.' },
  'Gray': { label: 'Gray', value: -1, description: 'Age-related silver-gray hair; only matches exactly.' },
  'White': { label: 'White', value: -1, description: 'Fully white or silver hair; only matches exactly.' },
  'Salt and Pepper': { label: 'Salt and Pepper', value: -1, description: 'A mix of dark and gray/white hair; only matches exactly.' },
} as const;

/** Flat array of hair color names for use in zod enums and selects. */
export const HAIR_COLOR_OPTIONS = Object.keys(HAIR_COLOR_DEFINITIONS) as unknown as readonly [
  'Black', 'Dark Brown', 'Brown', 'Light Brown', 'Auburn', 'Red',
  'Strawberry Blonde', 'Blonde', 'Fake Blonde', 'Platinum Blonde',
  'Gray', 'White', 'Salt and Pepper',
];

/**
 * @deprecated Use HAIR_COLOR_DEFINITIONS[color].value instead.
 * Lightness values for hair colors. Negative values indicate "special" colors that only match exactly (age-related).
 */
export const HAIR_COLOR_LIGHTNESS: Record<string, number> = Object.fromEntries(
  Object.entries(HAIR_COLOR_DEFINITIONS).map(([key, def]) => [key, def.value])
);

